import { Box } from "@chakra-ui/react";
import BranchSelection from "./components/BranchSelection";
import ReportTypesSelection from "./components/ReportTypesSelection";
import ReportLevelSelection from "./components/ReportLevelSelection";
import { useDispatch, useSelector } from "react-redux";
import {
  LEVELS,
  REPORT_TYPE_IDS,
  REPORT_TYPE_IDS_WITH_TABLE_GROUPING,
} from "../../../../store/constants";
import Save from "./components/Save";
import ReportPresentationControls from "../../ReportPresentationControls";
import {
  setActiveExportConfig,
  setActiveRenderConfig,
} from "../../../../store/actions/subscriptionsActions";
import {
  ARRIVAL_RATES_PER_SERVICE_COLUMN_DEFINITIONS,
  ARRIVAL_RATES_SUMMARY_COLUMN_DEFINITIONS,
  AGENT_TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
  CUSTOMERS_IN_BRANCH_COLUMN_DEFINITIONS,
  SERVICE_TIME_PER_SERVICE_COLUMN_DEFINITIONS,
  TICKET_SUMMARY_COLUMN_DEFINITIONS,
  TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
  WAITING_TIME_SUMMARY_COLUMN_DEFINITIONS,
  WAITING_TIME_PER_SERVICE_COLUMN_DEFINITIONS,
} from "../../../../store/renderConfig";

const AGENT_TICKETS_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_branch_agent_tickets_per_service,
  REPORT_TYPE_IDS.weekly_branch_agent_tickets_per_service,
  REPORT_TYPE_IDS.daily_branch_agent_tickets_per_service,
  REPORT_TYPE_IDS.monthly_branch_tickets_per_agent,
  REPORT_TYPE_IDS.weekly_branch_tickets_per_agent,
  REPORT_TYPE_IDS.daily_branch_tickets_per_agent,
]);

const NO_CHART_AGENT_TICKETS_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_branch_agent_tickets_per_service,
  REPORT_TYPE_IDS.weekly_branch_agent_tickets_per_service,
  REPORT_TYPE_IDS.daily_branch_agent_tickets_per_service,
]);

const TICKETS_PER_SERVICE_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_branch_tickets_per_service,
  REPORT_TYPE_IDS.weekly_branch_tickets_per_service,
  REPORT_TYPE_IDS.daily_branch_tickets_per_service,
  REPORT_TYPE_IDS.monthly_organization_tickets_per_service,
  REPORT_TYPE_IDS.weekly_organization_tickets_per_service,
  REPORT_TYPE_IDS.daily_organization_tickets_per_service,
]);

const SERVICE_TIME_PER_SERVICE_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_branch_service_time_per_service,
  REPORT_TYPE_IDS.weekly_branch_service_time_per_service,
  REPORT_TYPE_IDS.daily_branch_service_time_per_service,
  REPORT_TYPE_IDS.monthly_organization_service_time_per_service,
  REPORT_TYPE_IDS.weekly_organization_service_time_per_service,
  REPORT_TYPE_IDS.daily_organization_service_time_per_service,
]);

const WAITING_TIME_PER_SERVICE_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_branch_waiting_time_per_service,
  REPORT_TYPE_IDS.weekly_branch_waiting_time_per_service,
  REPORT_TYPE_IDS.daily_branch_waiting_time_per_service,
  REPORT_TYPE_IDS.monthly_organization_waiting_time_per_service,
  REPORT_TYPE_IDS.weekly_organization_waiting_time_per_service,
  REPORT_TYPE_IDS.daily_organization_waiting_time_per_service,
]);

const ARRIVAL_RATES_PER_SERVICE_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_branch_arrival_rates_per_service,
  REPORT_TYPE_IDS.weekly_branch_arrival_rates_per_service,
  REPORT_TYPE_IDS.daily_branch_arrival_rates_per_service,
  REPORT_TYPE_IDS.monthly_organization_arrival_rates_per_service,
  REPORT_TYPE_IDS.weekly_organization_arrival_rates_per_service,
  REPORT_TYPE_IDS.daily_organization_arrival_rates_per_service,
]);

const ARRIVAL_RATES_SUMMARY_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_branch_arrival_rates_per_day,
  REPORT_TYPE_IDS.weekly_branch_arrival_rates_per_day,
  REPORT_TYPE_IDS.monthly_organization_arrival_rates_per_day,
  REPORT_TYPE_IDS.weekly_organization_arrival_rates_per_day,
  REPORT_TYPE_IDS.monthly_branch_arrival_rates_per_hour,
  REPORT_TYPE_IDS.weekly_branch_arrival_rates_per_hour,
  REPORT_TYPE_IDS.daily_branch_arrival_rates_per_hour,
  REPORT_TYPE_IDS.monthly_organization_arrival_rates_per_hour,
  REPORT_TYPE_IDS.weekly_organization_arrival_rates_per_hour,
  REPORT_TYPE_IDS.daily_organization_arrival_rates_per_hour,
]);

const TICKET_SUMMARY_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_organization_tickets,
  REPORT_TYPE_IDS.weekly_organization_tickets,
  REPORT_TYPE_IDS.daily_organization_tickets,
  REPORT_TYPE_IDS.monthly_organization_tickets_per_branch,
  REPORT_TYPE_IDS.weekly_organization_tickets_per_branch,
  REPORT_TYPE_IDS.daily_organization_tickets_per_branch,
]);

const WAITING_TIME_SUMMARY_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_organization_waiting_time,
  REPORT_TYPE_IDS.weekly_organization_waiting_time,
  REPORT_TYPE_IDS.daily_organization_waiting_time,
]);

const CUSTOMERS_IN_BRANCH_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_branch_customers_in_branch_per_hour,
  REPORT_TYPE_IDS.weekly_branch_customers_in_branch_per_hour,
  REPORT_TYPE_IDS.daily_branch_customers_in_branch_per_hour,
]);

const ReportForm = () => {
  const dispatch = useDispatch();
  const {
    level,
    activeReportTypeId,
    editReportTypeId,
    activeRenderConfig,
    activeExportConfig,
  } = useSelector((state) => state.subscriptionsReducer);
  const reportTypeId = activeReportTypeId || editReportTypeId;
  const usesAgentTicketColumns = AGENT_TICKETS_REPORT_TYPES.has(reportTypeId);
  const usesServiceTicketColumns = TICKETS_PER_SERVICE_REPORT_TYPES.has(reportTypeId);
  const usesServiceTimeColumns = SERVICE_TIME_PER_SERVICE_REPORT_TYPES.has(reportTypeId);
  const usesWaitingTimeColumns = WAITING_TIME_PER_SERVICE_REPORT_TYPES.has(reportTypeId);
  const usesArrivalServiceColumns = ARRIVAL_RATES_PER_SERVICE_REPORT_TYPES.has(reportTypeId);
  const usesArrivalSummaryColumns = ARRIVAL_RATES_SUMMARY_REPORT_TYPES.has(reportTypeId);
  const usesTicketSummaryColumns = TICKET_SUMMARY_REPORT_TYPES.has(reportTypeId);
  const usesWaitingSummaryColumns = WAITING_TIME_SUMMARY_REPORT_TYPES.has(reportTypeId);
  const usesCustomersInBranchColumns = CUSTOMERS_IN_BRANCH_REPORT_TYPES.has(reportTypeId);
  const supportsCharts = !NO_CHART_AGENT_TICKETS_REPORT_TYPES.has(reportTypeId);
  const columnDefinitions = usesAgentTicketColumns
    ? AGENT_TICKETS_PER_SERVICE_COLUMN_DEFINITIONS
    : usesServiceTicketColumns
      ? TICKETS_PER_SERVICE_COLUMN_DEFINITIONS
      : usesServiceTimeColumns
        ? SERVICE_TIME_PER_SERVICE_COLUMN_DEFINITIONS
        : usesWaitingTimeColumns
          ? WAITING_TIME_PER_SERVICE_COLUMN_DEFINITIONS
          : usesArrivalServiceColumns
            ? ARRIVAL_RATES_PER_SERVICE_COLUMN_DEFINITIONS
            : usesArrivalSummaryColumns
              ? ARRIVAL_RATES_SUMMARY_COLUMN_DEFINITIONS
              : usesTicketSummaryColumns
                ? TICKET_SUMMARY_COLUMN_DEFINITIONS
                : usesWaitingSummaryColumns
                  ? WAITING_TIME_SUMMARY_COLUMN_DEFINITIONS
                  : usesCustomersInBranchColumns
                    ? CUSTOMERS_IN_BRANCH_COLUMN_DEFINITIONS
              : undefined;
  return (
    <Box>
      <ReportLevelSelection />
      <ReportTypesSelection />
      {level === LEVELS.BRANCH ? <BranchSelection /> : null}
      {activeReportTypeId || editReportTypeId ? (
        <ReportPresentationControls
          variant="subscription"
          renderConfig={activeRenderConfig}
          onChange={(config) => dispatch(setActiveRenderConfig(config))}
          exportConfig={activeExportConfig}
          onExportConfigChange={(config) => dispatch(setActiveExportConfig(config))}
          supportsTableGrouping={REPORT_TYPE_IDS_WITH_TABLE_GROUPING.includes(reportTypeId)}
          supportsCharts={supportsCharts}
          columnDefinitions={columnDefinitions}
          defaultSortColumn={
            usesServiceTimeColumns
              ? "totalServiceTime"
              : usesWaitingTimeColumns
                ? "totalWaitingTime"
              : usesWaitingSummaryColumns
                ? "totalWaitingTime"
              : usesAgentTicketColumns || usesServiceTicketColumns || usesArrivalServiceColumns || usesArrivalSummaryColumns || usesTicketSummaryColumns
                ? "total"
                : "average"
          }
        />
      ) : null}
      <Save/>
    </Box>
  );
};

export default ReportForm;
